import React, { useState } from "react";
import {
  Button,
  Form,
  FloatingLabel,
  Spinner,
  Container,
  Row,
  Col,
  Card,
  Image,
} from "react-bootstrap";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useNavigate, useLocation, Link } from "react-router-dom"; // Import useLocation for accessing token

import { useNotification } from "../../components/notification";
import apiService from "../../services/apiService";

import logo from "../../logo.png";

// Define validation schema using yup
const schema = yup.object().shape({
  newPassword: yup
    .string()
    .required("New password is required")
    .min(8, "Password must be at least 8 characters")
    .max(20, "Password must be at most 20 characters")
    .matches(
      /^(?=.*[A-Za-z])(?=.*\d)(?=.*[!@#$%^&*()_+{}|:"<>?~`\-=[\]\\;',./])/,
      "Password must contain at least one letter, one number, and one special character"
    ),
  confirmPassword: yup
    .string()
    .required("Confirm new password is required")
    .oneOf([yup.ref("newPassword"), null], "Passwords must match"),
});

const ResetPassword = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema), // Integrate yup with react-hook-form
  });
  const { showNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate(); // Initialize useNavigate
  const location = useLocation(); // To access the token from URL or state

  // Extract token and email from the URL or location state
  const queryParams = new URLSearchParams(location.search);
  const resetToken = queryParams.get("token");

  const onSubmit = async (data) => {
    try {
      if (!resetToken) {
        showNotification("danger", "Invalid or expired reset token.", 5000);
        return;
      }
      setIsLoading(true);

      await apiService.post("/auth/reset-password", {
        token: resetToken,
        password: data.newPassword,
      });

      navigate("/reset-password-confirmation");
    } catch (error) {
      showNotification(
        "danger",
        error.message || "Failed to reset password.",
        5000
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container className="min-vh-100 d-flex align-items-center justify-content-center flex-column">
      <Row>
        <Col xs={12} className="text-center mb-4">
          <Image
            src={logo}
            alt={`${process.env.REACT_APP_PORTAL}`}
            fluid
            width="225px"
          />
        </Col>
        <Col xs={11} md={8} lg={6} xl={6} xxl={6} className="m-auto">
          <Card className="p-4 shadow border-0">
            <Card.Body>
              <h1 className="text-center mb-3 fs-5">Reset Password</h1>
              <p className="text-center mb-4 text-light-emphasis fw-light">
                Please enter your new password below.
              </p>
              <Form onSubmit={handleSubmit(onSubmit)}>
                <FloatingLabel
                  controlId="txtNewPassword"
                  label="New Password"
                  className="mb-4"
                >
                  <Form.Control
                    type="password"
                    placeholder="New Password"
                    {...register("newPassword")}
                    isInvalid={!!errors.newPassword}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.newPassword?.message}
                  </Form.Control.Feedback>
                </FloatingLabel>

                <FloatingLabel
                  controlId="txtConfirmPassword"
                  label="Confirm Password"
                  className="mb-4"
                >
                  <Form.Control
                    type="password"
                    placeholder="Confirm Password"
                    {...register("confirmPassword")}
                    isInvalid={!!errors.confirmPassword}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.confirmPassword?.message}
                  </Form.Control.Feedback>
                </FloatingLabel>

                <div className="d-grid gap-2">
                  <Button
                    variant="primary"
                    disabled={isLoading}
                    type="submit"
                    className="btn-main"
                  >
                    {isLoading ? (
                      <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </Spinner>
                    ) : (
                      "Reset Password"
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
          <div className="text-center mt-3 text-light-emphasis fw-light">
            Remember Password?{" "}
            <Link to="/login" className="text-decoration-none link-secondary">
              Login
            </Link>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default ResetPassword;
